import {FieldError, Input, Label, type PressEvent, TextField} from 'react-aria-components'

import ChevronDownSvg from '@/assets/icons/chevron-down.svg?react'
import InfoSvg from '@/assets/icons/info.svg?react'
import lockSvg from '@/assets/icons/lock.svg'
import PriceHistorySvg from '@/assets/icons/price-history.svg?react'
import stripeTrustBadgePng from '@/assets/images/stripe-trust-badge.png'
import {useIsAuthenticatedValue} from '@/auth/hooks/useIsAuthenticated'
import Price from '@/components/Price'
import PrimaryButton from '@/components/PrimaryButton/PrimaryButton'
import Switch from '@/components/Switch/Switch'
import {USD_DISPLAY_DECIMALS} from '@/constants/config'
import type useCartSummary from '@/lib/cart/hooks/useCartSummary'
import ExpressCheckout from '@/lib/payment/components/ExpressCheckout'
import {useRoundUp} from '@/lib/payment/store'
import {PaymentRoute} from '@/routeRegistry'
import VisuallyHidden from '@/utils/a11y/VisuallyHidden'
import {useIsMobile} from '@/utils/hooks/useWindowSize'
import style from '@/views/Cart/Cart.module.scss'

interface Props {
  onSubmit: (e: PressEvent) => void
  isPending: boolean
  isRewardsApplied: boolean
  setIsRewardsApplied: Dispatch<boolean>
  cartSummary: ReturnType<typeof useCartSummary>
}
export default memo<Props>(function PaymentSummary({
  onSubmit,
  isPending,
  isRewardsApplied,
  cartSummary,
  setIsRewardsApplied,
}) {
  const isMobile = useIsMobile()
  const [isOpen, setIsOpen] = useState(!isMobile)
  const isAuthenticated = useIsAuthenticatedValue()
  const {method} = PaymentRoute.useSearch()
  const [roundUp, setRoundUp] = useRoundUp()

  const {
    cashback,
    cartItemNumber,
    subtotal,
    total,
    tax,
    discountValue,
    derivedDiscountValue,
    isTaxLoading,
    isLoading,
    custom,
    coupon,
    couponInfo,
    applyCoupon,
    couponError,
    handleCouponChange,
    isCouponLoading,
    totalAfterTax,
    roundUpAmount,
  } = cartSummary

  const isCouponValid = couponError === null

  return (
    <div className={style.CartSummary}>
      <h2 className={style.Title}>
        Order summary{' '}
        <button
          className={style.ShowHide}
          aria-label='Order summary'
          aria-expanded={isOpen}
          onClick={() => {
            setIsOpen(isOpen => !isOpen)
          }}
        >
          <ChevronDownSvg className={style.Decoration} aria-hidden />
        </button>
      </h2>
      {isOpen && (
        <>
          <div className={style.SumCategory}>
            <span className={style.SumCategoryName}>
              Subtotal ({cartItemNumber} item{cartItemNumber > 1 && 's'})
            </span>
            <span className={style.SumCategoryValue}>
              <Price
                price={subtotal}
                options={{
                  fractionDigits: USD_DISPLAY_DECIMALS,
                  exactFractionDigits: true,
                }}
              />
            </span>
          </div>
          <div className={style.Divider} />
          <div className={style.SumCategory}>
            <span className={clsx(style.SumCategoryName)}>Tax</span>
            <span className={clsx(style.SumCategoryValue)}>
              {isTaxLoading ? (
                '...'
              ) : (
                <Price
                  price={tax}
                  options={{
                    fractionDigits: USD_DISPLAY_DECIMALS,
                    exactFractionDigits: true,
                  }}
                />
              )}
            </span>
          </div>
          {roundUpAmount.gt(0) && (
            <>
              <div className={style.SumCategory}>
                <span className={clsx(style.SumCategoryName)}>Total after tax</span>
                <span className={clsx(style.SumCategoryValue)}>
                  <Price
                    price={totalAfterTax}
                    options={{
                      fractionDigits: USD_DISPLAY_DECIMALS,
                      exactFractionDigits: true,
                    }}
                  />
                </span>
              </div>
              <div className={clsx(style.SumCategory, style.Secondary)}>
                <div className={style.SwitchWrapper}>
                  <Switch
                    label='Donate change to NFB'
                    aria-label='Donate change to NFB'
                    onChange={setRoundUp}
                    isSelected={roundUp}
                    isLabelVisible
                    labelClassName={style.SumCategoryName}
                  />
                  <a
                    href='https://nfb.org/about-us/press-room/innosearch-ai-and-national-federation-blind-announce-strategic-partnership'
                    className={style.Info}
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    <InfoSvg aria-label='Read more about NFB change donation program' />
                  </a>
                </div>
                <span
                  className={clsx(
                    style.SumCategoryValue,
                    roundUp ? style.RoundedUp : style.UnRounded,
                  )}
                >
                  <Price
                    price={roundUpAmount}
                    options={{
                      fractionDigits: USD_DISPLAY_DECIMALS,
                      exactFractionDigits: true,
                    }}
                  />
                </span>
              </div>
            </>
          )}
          {!custom && isAuthenticated && isRewardsApplied && discountValue > 0 && (
            <div className={style.SumCategory}>
              <span className={clsx(style.SumCategoryName, style.GoodValue)}>Rewards applied</span>
              <span
                className={clsx(
                  style.SumCategoryValue,
                  derivedDiscountValue > 0 && style.GoodValue,
                )}
              >
                <Price
                  price={-derivedDiscountValue}
                  options={{
                    fractionDigits: USD_DISPLAY_DECIMALS,
                    exactFractionDigits: true,
                  }}
                />
              </span>
            </div>
          )}
          {custom && (
            <>
              {isAuthenticated && discountValue > 0 && (
                <div className={style.SumCategory}>
                  <Switch
                    label='Apply rewards'
                    aria-label='Apply rewards'
                    onChange={setIsRewardsApplied}
                    isSelected={isRewardsApplied}
                    isLabelVisible
                    labelClassName={style.SumCategoryName}
                  />
                  {isRewardsApplied && (
                    <span
                      className={clsx(
                        style.SumCategoryValue,
                        derivedDiscountValue > 0 && style.GoodValue,
                      )}
                    >
                      <Price
                        price={-derivedDiscountValue}
                        options={{
                          fractionDigits: USD_DISPLAY_DECIMALS,
                          exactFractionDigits: true,
                        }}
                      />
                    </span>
                  )}
                </div>
              )}
              <VisuallyHidden
                as='form'
                className={style.CouponForm}
                onSubmit={e => {
                  e.preventDefault()
                  applyCoupon()
                }}
                isVisible={!isRewardsApplied}
              >
                <TextField
                  name='coupon'
                  onChange={handleCouponChange}
                  validationBehavior='aria'
                  isInvalid={!isCouponValid}
                  type='text'
                  className={style.Field}
                  isDisabled={isRewardsApplied}
                >
                  <VisuallyHidden as={Label}>Coupon code</VisuallyHidden>
                  <Input className={style.Input} placeholder='Coupon code' />
                  {couponError && (
                    <FieldError className={style.Error}>{couponError.message}</FieldError>
                  )}
                </TextField>
                <PrimaryButton
                  type='submit'
                  size='sm'
                  className={style.ApplyBtn}
                  isPending={isCouponLoading}
                >
                  Apply
                </PrimaryButton>
              </VisuallyHidden>
            </>
          )}
          {!isRewardsApplied && couponInfo && (
            <div className={style.CouponInfo}>
              <div>Coupon applied</div>
              <div>
                <Price
                  price={-couponInfo.amount}
                  options={{
                    fractionDigits: USD_DISPLAY_DECIMALS,
                    exactFractionDigits: true,
                  }}
                />
              </div>
            </div>
          )}
        </>
      )}
      <div className={style.Total}>
        <span>Total</span>
        <span>
          <Price
            price={total}
            options={{
              fractionDigits: USD_DISPLAY_DECIMALS,
              exactFractionDigits: true,
            }}
          />
        </span>
      </div>
      {isOpen && cashback && (
        <div className={style.SumCategory}>
          <span className={clsx(style.SumCategoryName, style.GoodValue, style.Strong)}>
            <PriceHistorySvg className={style.Decoration} aria-hidden />
            Rewards ({cashback.rate.mul(100).toNumber()}%)
          </span>
          <span className={clsx(style.SumCategoryValue, style.GoodValue)}>
            <Price
              price={cashback.value}
              options={{
                fractionDigits: USD_DISPLAY_DECIMALS,
                exactFractionDigits: true,
              }}
            />
          </span>
        </div>
      )}
      {!method && custom && (
        <ExpressCheckout
          applyRewards={isRewardsApplied}
          custom
          className={style.ExpressCheckout}
          coupon={coupon}
        />
      )}
      <PrimaryButton
        className={style.PayBtn}
        onPress={onSubmit}
        isDisabled={cartItemNumber === 0 || isLoading}
        isPending={isPending}
      >
        {method && !isPending && <img className={style.LockIcon} src={lockSvg} alt='' />}
        {(() => {
          if (!method && custom) return 'Pay with Card'
          if (!method) return 'Next step'
          if (isPending) return 'Paying...'
          return 'Pay now'
        })()}
      </PrimaryButton>
      {isOpen && <img src={stripeTrustBadgePng} alt='' />}
    </div>
  )
})
